import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Reply } from "lucide-react";

/**
 * Tiny dashboard widget surfacing the per-pitch trackable reply funnel:
 *   X pitched · Y replied · Z% reply rate
 *
 * Reads GET /api/synergies/reply-stats. Hidden until at least one synergy has a share token.
 */
export default function ReplyStatsWidget() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/synergies/reply-stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
    }, []);

    if (!stats || stats.pitched === 0) return null;
    const rate = stats.reply_rate_pct || 0;
    const tone = rate >= 30 ? "text-emerald-400" : rate >= 10 ? "text-amber-400" : "text-zinc-400";

    return (
        <div className="border border-zinc-800 bg-zinc-950 p-5" data-testid="reply-stats-widget">
            <div className="flex items-center gap-2 mb-3">
                <Reply className="w-4 h-4 text-emerald-500" />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Pitch reply funnel</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
                <Cell icon={Mail} label="Pitched" value={stats.pitched} testid="reply-pitched" />
                <Cell icon={Reply} label="Replied" value={stats.replied} testid="reply-replied" emerald />
                <Cell label="Reply rate" value={`${rate}%`} testid="reply-rate" customTone={tone} />
            </div>
            <div className="font-mono text-[9px] text-zinc-600 mt-3 leading-snug">
                Each pitched synergy gets a unique reply alias. Manually mark replied on the synergy detail page until inbound forwarding is configured.
            </div>
        </div>
    );
}

function Cell({ icon: Icon, label, value, testid, emerald, customTone }) {
    const valueClass = customTone || (emerald ? "text-emerald-400" : "text-zinc-50");
    return (
        <div className="border border-zinc-800 bg-black/40 p-3" data-testid={testid}>
            <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
                {Icon && <Icon className="w-3 h-3" />} {label}
            </div>
            <div className={`font-heading font-black text-2xl tracking-tighter ${valueClass}`}>{value}</div>
        </div>
    );
}
